/**
 * When a held recording is read, and what happens when a read fails. Pure and
 * Angular-free so the rules can be tested without a clock or a network.
 *
 * Reading is not something the rep asks for. A recording made in a basement is
 * read when the device next has a connection, which may be minutes later and in
 * the middle of something else — so the rules here are about not getting in the
 * way as much as about making progress.
 */

/**
 * How long to wait after each successive failure. Capped rather than unbounded:
 * a service that has been down for an hour is no more likely to answer on a
 * twelve-hour wait than a fifteen-minute one, and the rep is still waiting.
 */
export const RETRY_WAITS_MS = [30_000, 60_000, 120_000, 300_000, 900_000];

export function waitAfterFailures(failures: number): number {
  const index = Math.min(Math.max(failures, 1), RETRY_WAITS_MS.length) - 1;
  return RETRY_WAITS_MS[index];
}

export interface SweepConditions {
  /** A connection is available now. */
  online: boolean;
  /** The rep is dictating; the device has one recogniser and they have it. */
  capturing: boolean;
  /** A read is already running. */
  reading: boolean;
  /** Recordings are waiting to be read. */
  waiting: number;
}

/**
 * Whether to start reading now. One at a time, and never while the rep is
 * speaking: taking the recogniser out from under a live dictation to read an old
 * recording trades what they are saying for what they already said.
 */
export function shouldSweep(conditions: SweepConditions | null | undefined): boolean {
  if (!conditions) {
    return false;
  }
  return conditions.online
    && !conditions.capturing
    && !conditions.reading
    && conditions.waiting > 0;
}

export interface AttemptConditions {
  /** Failures so far for this recording. */
  failures: number;
  /** When the last attempt failed. */
  lastFailedAt: number | null;
  now: number;
}

/** Whether this recording has waited long enough since its last failure. */
export function isDueForRetry(conditions: AttemptConditions | null | undefined): boolean {
  if (!conditions) {
    return false;
  }
  if (!conditions.failures || conditions.lastFailedAt == null) {
    return true;
  }
  return conditions.now - conditions.lastFailedAt >= waitAfterFailures(conditions.failures);
}
